// rfc6902 version 3.0.4 ist 100x schneller als 4.0.3
import { applyPatch, createPatch } from 'rfc6902';
import { isJSON } from '@st/utils-js';

function isPlainObject(value) {
  if (value === null || typeof value !== 'object') return false;
  // keep asymmetric matchers untouched so matcher based tests do not break
  if (
    '$$typeof' in value ||
    typeof value.asymmetricMatch === 'function' ||
    typeof value.toAsymmetricMatcher === 'function'
  ) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function cloneJsonLike(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => cloneJsonLike(entry));
  }

  if (isPlainObject(value)) {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = cloneJsonLike(value[key]);
      return acc;
    }, {});
  }

  return value;
}

function deepFreezeJsonLike(value) {
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      deepFreezeJsonLike(entry);
    });

    return Object.freeze(value);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  Object.keys(value).forEach((key) => {
    deepFreezeJsonLike(value[key]);
  });

  return Object.freeze(value);
}

export default class Patcher {
  /**
   *
   * @param {JSON} oldObj
   * @param {JSON} newObj
   * @returns patches to transform the old JSON to the new one
   */
  static createPatches(oldObj, newObj) {
    // snapshot inputs first so diffing always works on stable data
    const oldSnapshot = cloneJsonLike(oldObj);
    const newSnapshot = cloneJsonLike(newObj);

    // clone each patch entry so later source mutations cannot alter queued patches
    const patches = createPatch(oldSnapshot, newSnapshot).map((patch) =>
      Object.keys(patch).reduce((acc, key) => {
        acc[key] = cloneJsonLike(patch[key]);
        return acc;
      }, {})
    );

    // freeze json-like patch values so they stay stable while queueing
    return deepFreezeJsonLike(patches);
  }

  /**
   *
   * @param {Object} obj1
   * @param {Object} obj2
   * @returns true if the objects are equal;
   */
  static compare(obj1, obj2) {
    function isObject(value) {
      return value !== undefined && typeof value === 'object';
    }
    if (isObject(obj1) && isObject(obj2)) {
      return Patcher.createPatches(obj1, obj2).length === 0;
    }
    return false;
  }

  /**
   *
   * @param {JSON} oldObj
   * @param {Array} patches
   * @returns [null] if successfully updated
   */
  static applyPatches(oldObj, patches) {
    if (!isJSON(oldObj) || !Array.isArray(patches)) return oldObj;
    if (patches.length === 0) return [null];

    return applyPatch(oldObj, patches);
  }
}
